/**
 * Fixed-term class payment plans — the single source for allowed values, labels, and
 * the value → installment-count parser.
 *
 * Plan: docs/plans/2026-09-05-001-feat-fixed-term-monthly-installments-plan.md (KTD3)
 *
 * Pure module (no imports) so server routes, crons, and client pages can all import it.
 * The value set mirrors the DB CHECK `classes_session_payment_plan_check`
 * (migration 20261014_001); widen both together.
 */

export const SESSION_PAYMENT_PLAN_OPTIONS = {
  full_upfront: 'Pay in full (upfront)',
  split_2: 'Split into 2 monthly payments',
  split_3: 'Split into 3 monthly payments',
  split_4: 'Split into 4 monthly payments',
  split_5: 'Split into 5 monthly payments',
  split_6: 'Split into 6 monthly payments',
} as const;

export type SessionPaymentPlan = keyof typeof SESSION_PAYMENT_PLAN_OPTIONS;

export const SESSION_PAYMENT_PLAN_VALUES = Object.keys(
  SESSION_PAYMENT_PLAN_OPTIONS,
) as readonly SessionPaymentPlan[];

/** Largest installment count any plan value allows. */
export const MAX_SESSION_INSTALLMENTS = 6;

export function isSessionPaymentPlan(value: unknown): value is SessionPaymentPlan {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SESSION_PAYMENT_PLAN_OPTIONS, value);
}

/**
 * Turn a plan value into an installment count: `full_upfront` → 1, `split_N` → N.
 * Returns null for anything not in the allowed set — callers must reject, never
 * treat an unknown value as pay-in-full.
 */
export function installmentCountFromPlan(plan: unknown): number | null {
  if (!isSessionPaymentPlan(plan)) return null;
  if (plan === 'full_upfront') return 1;
  const n = Number(plan.slice('split_'.length));
  return Number.isInteger(n) && n >= 2 && n <= MAX_SESSION_INSTALLMENTS ? n : null;
}
